import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderCell,
  HeaderControlCell,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPhysicianOrderForm,
  updatePhysicianOrderForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  CommentsIcon,
  DeleteIcon,
  DoctorIcon,
  MapPinIcon,
  MedicalNotesIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import {
  buildInsurances,
  buildPhysicians,
} from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  ORDER_BLOCK_CATEGORIES,
  ORDER_CATEGORY_BY_VALUE,
  ORDER_MODES,
  VISIT_FREQUENCY_ROWS,
  newOrderBlockId,
} from "./physicianOrderContent";

const ORDER_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  orderDate: today(),
  companyName: "",
  companyAddress: "",
  city: "",
  state: "",
  zipCode: "",
  phone: "",
  fax: "",
  primaryCarrier: "",
  policyIdNumber: "",
  physicianName: "",
  physicianNpi: "",
  physicianAddress: "",
  physicianPhone: "",
  physicianFax: "",
  messageToPhysician: "",
  orderMode: "verbal",
  personReceivingVerbalOrder: "",
  personGivingVerbalOrder: "",
  orderReceiptDate: "",
  orderReceiptTime: "",
  orderEffectiveDate: "",
  orderReadBackVerified: false,
  freqSkilledNursing: "",
  freqPhysicalTherapy: "",
  freqOccupationalTherapy: "",
  freqSpeechTherapy: "",
  freqHomeHealthAide: "",
  freqMedicalSocialWorker: "",
  freqOtherDiscipline: "",
  otherDisciplineLabel: "",
  orderBlocks: [],
  patientSignatureName: "",
  clinicianSignatureName: "",
  reviewComments: "",
});

const caregiverLabel = (form) => {
  const name = form?.filledByName || "";
  const role = form?.filledByRole || "";
  if (name && role) return `${name} (${role})`;
  return name || role || "—";
};

const PhysicianOrderForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [pendingCategory, setPendingCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const formTitle = form?.formName || clinicalForm?.formName || "Physician Order";

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      setValues({
        orderDate: data.orderDate || today(),
        companyName: data.companyName || "",
        companyAddress: data.companyAddress || "",
        city: data.city || "",
        state: data.state || "",
        zipCode: data.zipCode || "",
        phone: data.phone || "",
        fax: data.fax || "",
        primaryCarrier: data.primaryCarrier || "",
        policyIdNumber: data.policyIdNumber || "",
        physicianName: data.physicianName || "",
        physicianNpi: data.physicianNpi || "",
        physicianAddress: data.physicianAddress || "",
        physicianPhone: data.physicianPhone || "",
        physicianFax: data.physicianFax || "",
        messageToPhysician: data.messageToPhysician || "",
        orderMode: data.orderMode || "verbal",
        personReceivingVerbalOrder: data.personReceivingVerbalOrder || "",
        personGivingVerbalOrder: data.personGivingVerbalOrder || "",
        orderReceiptDate: data.orderReceiptDate || "",
        orderReceiptTime: data.orderReceiptTime || "",
        orderEffectiveDate: data.orderEffectiveDate || "",
        orderReadBackVerified: Boolean(data.orderReadBackVerified),
        freqSkilledNursing: data.freqSkilledNursing || "",
        freqPhysicalTherapy: data.freqPhysicalTherapy || "",
        freqOccupationalTherapy: data.freqOccupationalTherapy || "",
        freqSpeechTherapy: data.freqSpeechTherapy || "",
        freqHomeHealthAide: data.freqHomeHealthAide || "",
        freqMedicalSocialWorker: data.freqMedicalSocialWorker || "",
        freqOtherDiscipline: data.freqOtherDiscipline || "",
        otherDisciplineLabel: data.otherDisciplineLabel || "",
        orderBlocks: Array.isArray(data.orderBlocks)
          ? data.orderBlocks.map((block) => ({
              id: block.id || newOrderBlockId(),
              category: block.category,
              title: block.title || "",
              content: block.content || "",
              startEffectiveDate: block.startEffectiveDate || "",
              tags: Array.isArray(block.tags) ? block.tags : [],
            }))
          : [],
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Chart-side insurance / physician is only used to fill blanks on first
    // open; once the dedicated row has values, those win.
    const insurers = buildInsurances(patient);
    const physicians = buildPhysicians(patient);
    const primaryIns = insurers[0];
    const primaryPhys = physicians[0];

    if (isDraft) {
      hydrate({
        formName: clinicalForm.formName || draft?.formName || "Physician Order",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        ...draftCaregiverIdentity(currentUser, role),
        permissions: buildClinicalFormPermissions("pending", role),
        primaryCarrier: primaryIns?.insuranceName || "",
        policyIdNumber: primaryIns?.policyNumber || "",
        physicianName: primaryPhys?.physicianName || "",
        physicianNpi: primaryPhys?.npiNumber || "",
        physicianAddress: primaryPhys?.physicianAddress || "",
        physicianPhone: primaryPhys?.physicianPhone || "",
        physicianFax: primaryPhys?.physicianFax || "",
      });
      if (!cancelled) {
        setLoading(false);
        onReady?.();
      }
      return () => {
        cancelled = true;
      };
    }

    getPhysicianOrderForm(patientId, formId)
      .then((res) => {
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        hydrate({
          ...loaded,
          primaryCarrier:
            loaded.primaryCarrier || primaryIns?.insuranceName || "",
          policyIdNumber:
            loaded.policyIdNumber || primaryIns?.policyNumber || "",
          physicianName:
            loaded.physicianName || primaryPhys?.physicianName || "",
          physicianNpi: loaded.physicianNpi || primaryPhys?.npiNumber || "",
          physicianAddress:
            loaded.physicianAddress || primaryPhys?.physicianAddress || "",
          physicianPhone:
            loaded.physicianPhone || primaryPhys?.physicianPhone || "",
          physicianFax: loaded.physicianFax || primaryPhys?.physicianFax || "",
        });
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
    // Load once per form identity — unstable callbacks must not retrigger fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const updateBlock = (blockId, patch) => {
    setValues((prev) => ({
      ...prev,
      orderBlocks: prev.orderBlocks.map((block) =>
        block.id === blockId ? { ...block, ...patch } : block,
      ),
    }));
  };

  const removeBlock = (blockId) => {
    setValues((prev) => ({
      ...prev,
      orderBlocks: prev.orderBlocks.filter((block) => block.id !== blockId),
    }));
  };

  const addOrderBlock = () => {
    if (!pendingCategory) {
      toast.error("Select an order category to add");
      return;
    }
    const meta = ORDER_CATEGORY_BY_VALUE[pendingCategory];
    if (!meta) {
      toast.error("Unknown order category");
      return;
    }
    setValues((prev) => ({
      ...prev,
      orderBlocks: [
        ...prev.orderBlocks,
        {
          id: newOrderBlockId(),
          category: meta.value,
          title: meta.label.toUpperCase(),
          content: "",
          startEffectiveDate: values.orderEffectiveDate || today(),
          tags: [],
        },
      ],
    }));
    setPendingCategory("");
  };

  const toggleBlockTag = (blockId, tag) => {
    setValues((prev) => ({
      ...prev,
      orderBlocks: prev.orderBlocks.map((block) => {
        if (block.id !== blockId) return block;
        const has = block.tags.includes(tag);
        return {
          ...block,
          tags: has
            ? block.tags.filter((t) => t !== tag)
            : [...block.tags, tag],
        };
      }),
    }));
  };

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId: form?.filledBy || clinicalForm?.filledBy || (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });
  const isVerbal = values.orderMode === "verbal";

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const payload = {
      ...values,
      flag,
      orderDate: values.orderDate
        ? formatDateForInput(values.orderDate)
        : null,
      orderReceiptDate: values.orderReceiptDate
        ? formatDateForInput(values.orderReceiptDate)
        : null,
      orderEffectiveDate: values.orderEffectiveDate
        ? formatDateForInput(values.orderEffectiveDate)
        : null,
      orderMode: values.orderMode || null,
      orderBlocks: values.orderBlocks.map((block) => ({
        id: block.id,
        category: block.category,
        title: block.title || ORDER_CATEGORY_BY_VALUE[block.category]?.label || "Order",
        content: block.content || "",
        startEffectiveDate: block.startEffectiveDate
          ? formatDateForInput(block.startEffectiveDate)
          : null,
        tags: block.tags || [],
      })),
    };

    // Physician Order no longer captures a patient signature.
    delete payload.patientSignatureName;

    if (!canSign) return payload;
    const sig = signatures.buildSignaturePayload();
    delete sig.patientSignatureImage;
    delete sig.clearPatientSignature;
    return { ...payload, ...sig };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePhysicianOrderForm,
        filledByUserId,
        scheduleDate: values.orderDate
          ? formatDateForInput(values.orderDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {formTitle}
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <section className="rounded-md border border-slate-800 overflow-hidden">
          <div className="grid grid-cols-5 gap-x-2.5 items-start px-3 py-1.5">
            <HeaderCell
              label="Patient Name"
              value={patientName || form.patientName}
              dense
            />
            <HeaderCell label="MR Number" value={patient?.mrn} dense />
            <HeaderCell
              label="Date of Birth"
              value={displayDateUS(
                patient?.dateOfBirth || form.patientDateOfBirth,
              )}
              dense
            />
            {caregiverSlot || (
              <HeaderCell
                label="Caregiver Name"
                value={caregiverLabel(form)}
                dense
              />
            )}
            {printMode ? (
              <HeaderCell
                label="Date"
                value={displayDateUS(values.orderDate)}
                dense
              />
            ) : (
              <HeaderControlCell label="Date">
                <InputDatePicker
                  name="orderDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.orderDate) || ""}
                  onChange={(event) =>
                    setValue("orderDate", event.target.value)
                  }
                  max={ORDER_DATE_MAX}
                  className="min-w-0 w-full"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </HeaderControlCell>
            )}
          </div>
        </section>

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <Section icon={MapPinIcon} title="Company & Location Details">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Company Name" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.companyName}
                disabled={!canEdit}
                onChange={(e) => setValue("companyName", e.target.value)}
              />
            </Field>
            <Field label="Company Address" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.companyAddress}
                disabled={!canEdit}
                placeholder="Street address"
                onChange={(e) => setValue("companyAddress", e.target.value)}
              />
            </Field>
            <Field label="City" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.city}
                disabled={!canEdit}
                onChange={(e) => setValue("city", e.target.value)}
              />
            </Field>
            <Field label="State" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.state}
                disabled={!canEdit}
                onChange={(e) => setValue("state", e.target.value)}
              />
            </Field>
            <Field label="Zip" span="lg:col-span-2">
              <input
                type="text"
                className={fieldClass}
                value={values.zipCode}
                disabled={!canEdit}
                onChange={(e) => setValue("zipCode", e.target.value)}
              />
            </Field>
            <Field label="Phone" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.phone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("phone", e.target.value)}
              />
            </Field>
            <Field label="Fax" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.fax}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("fax", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={ShieldCheckIcon} title="Insurance Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            <Field label="Primary Carrier">
              <input
                type="text"
                className={fieldClass}
                value={values.primaryCarrier}
                disabled={!canEdit}
                onChange={(e) => setValue("primaryCarrier", e.target.value)}
              />
            </Field>
            <Field label="Policy / ID Number">
              <input
                type="text"
                className={fieldClass}
                value={values.policyIdNumber}
                disabled={!canEdit}
                onChange={(e) => setValue("policyIdNumber", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={DoctorIcon} title="Physician Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-3 gap-y-2">
            <Field label="Physician Name" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianName}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianName", e.target.value)}
              />
            </Field>
            <Field label="Physician NPI" span="lg:col-span-3">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianNpi}
                disabled={!canEdit}
                inputMode="numeric"
                maxLength={10}
                onChange={(e) =>
                  setValue(
                    "physicianNpi",
                    e.target.value.replace(/\D/g, "").slice(0, 10),
                  )
                }
              />
            </Field>
            <Field label="Physician Address" span="lg:col-span-6">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianAddress}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianAddress", e.target.value)}
              />
            </Field>
            <Field label="Physician Phone" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.physicianPhone}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("physicianPhone", e.target.value)}
              />
            </Field>
            <Field label="Physician Fax" span="lg:col-span-3">
              <input
                type="tel"
                className={fieldClass}
                value={values.physicianFax}
                disabled={!canEdit}
                placeholder="(000) 000-0000"
                onChange={(e) => setValue("physicianFax", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={CommentsIcon} title="Clinical Communication">
          <Field label="Message to Physician">
            <textarea
              className={`${textareaClass} min-h-[72px]`}
              value={values.messageToPhysician}
              disabled={!canEdit}
              placeholder="Type clinical updates or specific requests for the physician…"
              onChange={(e) => setValue("messageToPhysician", e.target.value)}
            />
          </Field>
        </Section>

        <Section icon={MedicalNotesIcon} title="Order Details">
          <div className="flex flex-wrap gap-1.5 mb-3">
            {ORDER_MODES.map((mode) => {
              const selected = values.orderMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => setValue("orderMode", mode.value)}
                  className={`px-3 py-1 rounded text-[11px] font-semibold border transition-colors ${
                    selected
                      ? "border-indigo-400 bg-indigo-600 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                  } disabled:opacity-60`}
                >
                  {mode.label}
                </button>
              );
            })}
          </div>

          {isVerbal && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2 mb-2">
              <Field label="Person Receiving Verbal Order">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.personReceivingVerbalOrder}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("personReceivingVerbalOrder", e.target.value)
                  }
                />
              </Field>
              <Field label="Person Giving Verbal Order">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.personGivingVerbalOrder}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("personGivingVerbalOrder", e.target.value)
                  }
                />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Order Receipt Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.orderReceiptDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="orderReceiptDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.orderReceiptDate) || ""}
                  onChange={(event) =>
                    setValue("orderReceiptDate", event.target.value)
                  }
                  max={ORDER_DATE_MAX}
                  className="w-full"
                  inputClassName={fieldClass}
                />
              )}
            </Field>
            <Field label="Order Receipt Time">
              <input
                type="time"
                className={fieldClass}
                value={values.orderReceiptTime}
                disabled={!canEdit}
                onChange={(e) => setValue("orderReceiptTime", e.target.value)}
              />
            </Field>
            <Field label="Order Effective Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.orderEffectiveDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="orderEffectiveDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.orderEffectiveDate) || ""}
                  onChange={(event) =>
                    setValue("orderEffectiveDate", event.target.value)
                  }
                  className="w-full"
                  inputClassName={fieldClass}
                />
              )}
            </Field>
          </div>

          {isVerbal && (
            <label
              className={`mt-3 flex items-start gap-2 text-[11px] leading-snug ${
                canEdit ? "cursor-pointer text-slate-700" : "text-slate-500"
              }`}
            >
              <input
                type="checkbox"
                className={checkboxClass}
                checked={Boolean(values.orderReadBackVerified)}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("orderReadBackVerified", e.target.checked)
                }
              />
              <span>Order read back and verified with Physician</span>
            </label>
          )}
        </Section>

        <Section icon={CalendarIcon} title="Visit Frequency & Disciplines">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="pb-1 text-left text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                  Discipline
                </th>
                <th className="pb-1 text-left text-[9px] font-semibold uppercase tracking-wide text-slate-500 w-40">
                  Frequency / Duration
                </th>
              </tr>
            </thead>
            <tbody>
              {VISIT_FREQUENCY_ROWS.map((row) => (
                <tr key={row.key} className="border-b border-slate-50">
                  <td className="py-1 pr-3 text-[11px] font-semibold text-slate-700">
                    {row.label}
                  </td>
                  <td className="py-1 w-40">
                    <input
                      type="text"
                      className={fieldClass}
                      value={values[row.key]}
                      disabled={!canEdit}
                      placeholder="e.g. 2w9"
                      maxLength={60}
                      aria-label={`${row.label} frequency`}
                      onChange={(e) => setValue(row.key, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
              <tr className="border-b border-slate-50">
                <td className="py-1 pr-3">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.otherDisciplineLabel}
                    disabled={!canEdit}
                    placeholder="Other discipline"
                    maxLength={120}
                    onChange={(e) =>
                      setValue("otherDisciplineLabel", e.target.value)
                    }
                  />
                </td>
                <td className="py-1 w-40">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.freqOtherDiscipline}
                    disabled={!canEdit}
                    placeholder="e.g. 1w4"
                    maxLength={60}
                    onChange={(e) =>
                      setValue("freqOtherDiscipline", e.target.value)
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Section icon={MedicalNotesIcon} title="Clinical Orders & Interventions">
          <SubHeading>Add an order section</SubHeading>
          {!printMode && canEdit && (
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-700 shrink-0">
                  Orders:
                </span>
                <select
                  className={`${fieldClass} flex-1`}
                  value={pendingCategory}
                  onChange={(e) => setPendingCategory(e.target.value)}
                  aria-label="Order category"
                >
                  <option value="">(Select)</option>
                  {ORDER_BLOCK_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={addOrderBlock}
                className="shrink-0 px-3 h-7 rounded text-[11px] font-semibold bg-indigo-600 text-white hover:bg-indigo-700"
              >
                Add to form
              </button>
            </div>
          )}

          {values.orderBlocks.length === 0 ? (
            <p className="text-[11px] text-slate-500">
              No clinical order sections added yet. Select a category above and
              add it to the form.
            </p>
          ) : (
            <div className="space-y-2.5">
              {values.orderBlocks.map((block) => {
                const meta = ORDER_CATEGORY_BY_VALUE[block.category];
                const tags = meta?.tags || [];
                return (
                  <div
                    key={block.id}
                    className="rounded-md border border-slate-200 bg-slate-50/40 overflow-hidden"
                  >
                    <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-slate-200 bg-white">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-800 truncate">
                          {block.title || meta?.label || "Order"}
                        </p>
                      </div>
                      {canEdit && !printMode && (
                        <button
                          type="button"
                          onClick={() => removeBlock(block.id)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-700 text-white hover:bg-indigo-800"
                        >
                          <DeleteIcon size={10} />
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="p-3 space-y-2">
                      {tags.length > 0 && canEdit && !printMode && (
                        <div className="flex flex-wrap gap-1.5">
                          {tags.map((tag) => {
                            const on = block.tags.includes(tag);
                            return (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => toggleBlockTag(block.id, tag)}
                                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                                  on
                                    ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                                    : "border-slate-200 bg-white text-slate-600"
                                }`}
                              >
                                {tag}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {block.tags?.length > 0 && (!canEdit || printMode) && (
                        <p className="text-[10px] text-slate-500">
                          {block.tags.join(" · ")}
                        </p>
                      )}
                      <textarea
                        className={`${textareaClass} min-h-[88px] bg-white`}
                        value={block.content}
                        disabled={!canEdit}
                        placeholder={
                          meta?.placeholder ||
                          "Enter order details for this section…"
                        }
                        onChange={(e) =>
                          updateBlock(block.id, { content: e.target.value })
                        }
                      />
                      <Field label="Start Effective Date">
                        {printMode ? (
                          <div className={`${fieldClass} flex items-center bg-white`}>
                            {displayDateUS(block.startEffectiveDate) || "—"}
                          </div>
                        ) : (
                          <InputDatePicker
                            name={`startEffectiveDate-${block.id}`}
                            formatSeparator="/"
                            allowManualEntry
                            disabled={!canEdit}
                            value={
                              formatDateForInput(block.startEffectiveDate) || ""
                            }
                            onChange={(event) =>
                              updateBlock(block.id, {
                                startEffectiveDate: event.target.value,
                              })
                            }
                            className="w-full max-w-xs"
                            inputClassName={`${fieldClass} bg-white`}
                          />
                        )}
                      </Field>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="Clinician Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

PhysicianOrderForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PhysicianOrderForm;
